import {
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-premium';
import React, { Dispatch, SetStateAction } from 'react';

import { convertMuiFilterForV2ApiWithoutValue, dayjs, isOwnerPortal } from '../../utils';

const numericOperators = getGridNumericOperators();
const dateOperators = getGridDateOperators();
const dateTimeOperators = getGridDateOperators(true);

const style = {
	background: 'none',
	border: 0,
	color: 'blue',
	fontSize: 'inherit',
	cursor: 'pointer',
};

export const defaultOrder = {
	field: 'createdAt',
	sort: 'desc',
};

export const facilityTimezone = {
	timezone: '',
};
export const accountHistoryColumns: GridColDef[] = [
	{
		field: 'createdAt',
		headerName: 'Transaction Date',
		filterOperators: dateTimeOperators,
		width: 150,
	},
	{
		field: 'applicationDate',
		headerName: 'Application Date',
		filterOperators: dateOperators,
		width: 150,
		renderCell: (params) =>
			params.row.applicationDate
				? dayjs(params.row.applicationDate.slice(0, 10), 'YYYY-MM-DD').format('MM/DD/YYYY')
				: '-',
	},
	{
		field: 'accountHistoryType',
		headerName: 'Type',
		width: 350,
	},
	{
		field: 'invoiceReadableId',
		headerName: 'Invoice #',
		renderCell: (params) =>
			params.row.invoiceReadableId !== '-' ? (
				<a
					target='_blank'
					style={style}
					href={
						isOwnerPortal
							? `/invoice-detail/${params.row.invoiceId}`
							: `/invoice-details/?invoiceId=${params.row.invoiceId}`
					}
					rel='noreferrer'
				>
					{params.row.invoiceReadableId}
				</a>
			) : (
				'-'
			),
		width: 200,
	},
	{
		field: 'amount',
		headerName: 'Invoice / Payment Amount',
		filterOperators: numericOperators,
		renderCell: (params) =>
			params.row.amount !== '-' ? (
				params.row.isNegative ? (
					<span style={{ color: 'red' }}>
						(
						{params.row.amount.toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
						)
					</span>
				) : (
					<span>
						{params.row.amount.toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</span>
				)
			) : (
				'-'
			),
		width: 150,
	},
	{
		field: 'dueOn',
		headerName: 'Due Date',
		filterOperators: dateOperators,
		width: 200,
	},
	{
		field: 'paymentType',
		headerName: 'Payment Type',
		width: 200,
	},
	{
		field: 'paymentMetaId',
		headerName: 'Payment ID',
		renderCell: (params) =>
			params.row.paymentMetaId && params.row.paymentGroupId && !isOwnerPortal ? (
				<a
					target='_blank'
					style={style}
					href={`/payment-detail/?paymentGroupId=${params.row.paymentGroupId}`}
					rel='noreferrer'
				>
					{params.row.paymentMetaId}
				</a>
			) : params.row.paymentMetaId ? (
				<span>{params.row.paymentMetaId}</span>
			) : (
				'-'
			),
		width: 250,
	},
	{
		field: 'creditReadableId',
		headerName: 'Credit ID',
		renderCell: (params) =>
			params.row.creditReadableId !== '-' || !isOwnerPortal ? (
				<a
					target='_blank'
					style={style}
					href={`/credit-detail/?creditId=${params.row.creditId}`}
					rel='noreferrer'
				>
					{params.row.creditReadableId}
				</a>
			) : (
				'-'
			),
		width: 200,
	},
	{
		field: 'creditAmount',
		headerName: 'Credit Amount',
		filterOperators: numericOperators,
		renderCell: (params) =>
			params.row.creditAmount !== '-' ? (
				params.row.isNegative ? (
					<span style={{ color: 'red' }}>
						(
						{params.row.creditAmount.toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
						)
					</span>
				) : (
					<span>
						{params.row.creditAmount.toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</span>
				)
			) : (
				'-'
			),
		width: 150,
	},
	{
		field: 'timezone',
		headerName: 'Facility Timezone',
		width: 250,
		filterable: false,
	},
];
export const accountHistoryOnSortModelChange =
	({
		setPaginationModel,
		setSortOrder,
	}: {
		setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
		setSortOrder: Dispatch<
			SetStateAction<{
				field: string;
				sort: string;
			}>
		>;
	}) =>
	(sortModel: GridSortModel) => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
		if (!sortModel.length) {
			return setSortOrder(defaultOrder);
		}

		const { field, sort } = sortModel[0];

		return setSortOrder({
			field,
			sort,
		});
	};

export const accountHistoryOnFilterModelChange =
	({
		setPaginationModel,
		setFilters,
	}: {
		setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
		setFilters: Dispatch<
			SetStateAction<
				{
					field: string;
					op: string;
					value: string;
				}[]
			>
		>;
	}) =>
	(filterModel: GridFilterModel) => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));

		const { items: filterItems } = filterModel;

		if (!filterItems.length) {
			setFilters([]);
			return;
		}

		const queryFilters = filterItems
			.map((filter) => {
				const { field, operator, value: rawValue = null } = filter;
				const op = convertMuiFilterForV2ApiWithoutValue(operator);
				if (!rawValue && field !== 'active' && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];
				let modifiedValue = rawValue;
				if (rawValue) {
					if (rawValue.toLowerCase().includes('gpi')) {
						modifiedValue = rawValue.replace(/\s/g, '_');
					}
					if (field.toLowerCase().includes('amount')) {
						modifiedValue = Number(rawValue) * 100;
					}
				}

				return {
					propertyName: field,
					op,
					value: modifiedValue,
					timezone: facilityTimezone.timezone,
				};
			})
			.flat();
		setFilters(queryFilters);
	};
